import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Radio,
  Stack,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  BalanceOutlined as BalanceOutlinedIcon,
  ViewColumnOutlined as ViewColumnOutlinedIcon,
  DirectionsBoatOutlined as DirectionsBoatOutlinedIcon,
  VerticalAlignTopOutlined as VerticalAlignTopOutlinedIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { updateVessel } from "../../data/vessels";
import type { VesselPlannerSetting } from "../../types/VesselPlannerSetting";
import MobileVesselPositionGrid from "../mobile/terminalCall/MobileVesselPositionGrid";
import {
  DEFAULT_VESSEL_PLANNER_SETTING,
  getUsableVesselSlots,
  normalizeVesselPlannerSetting,
  plannerPositionKey,
} from "./plannerSettings";

type VesselPlannerSettingsDialogProps = {
  open: boolean;
  vessel: any;
  onClose: () => void;
  onSaved?: (vessel: any) => void;
};

type VesselSettingsGridProps = {
  vessel: any;
  excluded: Set<string>;
  sequenceNumbers?: Map<string, number>;
  showSequenceNumbers?: boolean;
  disableExcluded?: boolean;
  onPositionClick: (x: number, y: number) => void;
};

const GROUPING_PREVIEW_COLORS = {
  orange: "#EFA06F",
  blue: "#8FC9DF",
  green: "#9BD27F",
  yellow: "#F1DF79",
};

const GROUPING_OPTIONS: Array<{
  value: VesselPlannerSetting["groupingPreference"];
  labelKey: string;
  descriptionKey: string;
  preview: string[];
}> = [
  {
    value: "fullBeamStrips",
    labelKey: "autoPlannerGrouping_fullBeamStrips",
    descriptionKey: "autoPlannerGrouping_fullBeamStrips_desc",
    preview: Array.from({ length: 4 }).flatMap(() => [
      GROUPING_PREVIEW_COLORS.orange,
      GROUPING_PREVIEW_COLORS.blue,
      GROUPING_PREVIEW_COLORS.green,
      GROUPING_PREVIEW_COLORS.blue,
      GROUPING_PREVIEW_COLORS.orange,
      GROUPING_PREVIEW_COLORS.blue,
    ]),
  },
  {
    value: "centerBlocks",
    labelKey: "autoPlannerGrouping_centerBlocks",
    descriptionKey: "autoPlannerGrouping_centerBlocks_desc",
    preview: [
      ...["orange", "yellow", "yellow", "yellow", "orange", "orange"],
      ...["blue", "green", "green", "blue", "green", "green"],
      ...["blue", "green", "green", "blue", "green", "green"],
      ...["orange", "yellow", "yellow", "yellow", "orange", "orange"],
    ].map(
      (color) =>
        GROUPING_PREVIEW_COLORS[
          color as keyof typeof GROUPING_PREVIEW_COLORS
        ]
    ),
  },
  {
    value: "smallPiles",
    labelKey: "autoPlannerGrouping_smallPiles",
    descriptionKey: "autoPlannerGrouping_smallPiles_desc",
    preview: [
      ...["yellow", "blue", "green", "yellow", "blue", "green"],
      ...["green", "orange", "green", "blue", "green", "orange"],
      ...["green", "blue", "green", "orange", "green", "blue"],
      ...["yellow", "blue", "green", "yellow", "green", "blue"],
    ].map(
      (color) =>
        GROUPING_PREVIEW_COLORS[
          color as keyof typeof GROUPING_PREVIEW_COLORS
        ]
    ),
  },
];

const VesselSettingsGrid = ({
  vessel,
  excluded,
  sequenceNumbers,
  showSequenceNumbers = false,
  disableExcluded = false,
  onPositionClick,
}: VesselSettingsGridProps) => {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState<number | undefined>();
  const floorVessel = useMemo(
    () => ({ ...vessel, positions: [vessel?.positions?.[0] || []] }),
    [vessel]
  );
  const dummyContainers = useMemo(
    () =>
      (vessel?.dummies || []).map((position: any, index: number) => ({
        "@id": `planner-setting-dummy-${index}`,
        uid: "",
        dummy: true,
        position: [1, Number(position?.[0]), Number(position?.[1])],
        containerType: { teu: 1 },
      })),
    [vessel]
  );
  const renderedRowCount = useMemo(
    () =>
      (floorVessel.positions?.[0] || []).reduce((maximum: number, value: any) => {
        const count = Number(value) || 0;
        const renderedCount = count === 2 ? 4 : count === 3 ? 5 : count;
        return Math.max(maximum, renderedCount);
      }, 0),
    [floorVessel]
  );

  useEffect(() => {
    const viewport = viewportRef.current;
    const planner = viewport?.querySelector<HTMLElement>(".mobile-position-planner");
    const grid = viewport?.querySelector<HTMLElement>(".mobile-position-grid-scroll");
    const ship = viewport?.querySelector<HTMLElement>(".mobile-position-ship");
    if (!viewport || !planner || !grid || !ship) return;

    const fitGrid = () => {
      const naturalWidth = ship.scrollWidth;
      const availableWidth = Math.max(0, viewport.clientWidth - 22);
      const nextScale = naturalWidth
        ? Math.min(1, availableWidth / naturalWidth)
        : 1;
      const nonGridHeight = planner.scrollHeight - grid.offsetHeight;
      setScale(nextScale);
      setHeight(Math.ceil(nonGridHeight + grid.offsetHeight * nextScale));
    };

    fitGrid();
    const resizeObserver = new ResizeObserver(fitGrid);
    resizeObserver.observe(viewport);
    resizeObserver.observe(ship);
    return () => resizeObserver.disconnect();
  }, [floorVessel]);

  return (
    <Box
      ref={viewportRef}
      sx={{
        boxSizing: "border-box",
        position: "relative",
        width: "100%",
        pl: "22px",
        overflow: "hidden",
        "& .mobile-position-controls": { display: "none" },
        "& .mobile-position-planner": { height, overflow: "hidden" },
        "& .mobile-position-grid-scroll": {
          overflowX: "visible",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        },
        "& .mobile-position-cell:not(:disabled)": { cursor: "pointer" },
        "& .planner-setting-excluded": {
          borderColor: "#aeb4bd",
          backgroundColor: "#d6d9df !important",
        },
        "& .planner-setting-sequenced": {
          borderColor: "#7da8e8",
          backgroundColor: "#e8f1ff !important",
          color: "#172554",
          fontSize: "0.55rem",
          fontWeight: 900,
        },
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          zIndex: 2,
          top: `${25 * scale}px`,
          left: 0,
          display: "flex",
          width: 18,
          flexDirection: "column",
          pointerEvents: "none",
        }}
      >
        {Array.from({ length: renderedRowCount }, (_, index) =>
          vessel?.preferredDockingSide === "starboard"
            ? renderedRowCount - index
            : index + 1
        ).map((row) => (
          <Typography
            component="span"
            key={row}
            sx={{
              display: "grid",
              height: `${20 * scale}px`,
              color: "#566270",
              fontSize: "0.58rem",
              fontWeight: 800,
              lineHeight: 1,
              placeItems: "center",
            }}
          >
            {row}
          </Typography>
        ))}
      </Box>
      <MobileVesselPositionGrid
        vessel={floorVessel}
        containers={dummyContainers}
        direction="loading"
        dockingSide={vessel?.preferredDockingSide || "port"}
        onPositionClick={([, x, y]) => onPositionClick(x, y)}
        getCellPresentation={({ column, slot, isGhost, isFiller, container }) => {
          const key = plannerPositionKey(column, slot);
          const sequenceNumber = sequenceNumbers?.get(key);
          const isExcluded = excluded.has(key);
          return {
            className: isExcluded
              ? "planner-setting-excluded"
              : sequenceNumber
                ? "planner-setting-sequenced"
                : undefined,
            content:
              showSequenceNumbers && sequenceNumber ? (
                <span>{sequenceNumber}</span>
              ) : undefined,
            disabled:
              isGhost ||
              isFiller ||
              Boolean(container?.dummy) ||
              (disableExcluded && isExcluded),
            ariaLabel: `Position ${column},${slot}`,
          };
        }}
      />
    </Box>
  );
};

const VesselPlannerSettingsDialog = ({
  open,
  vessel,
  onClose,
  onSaved,
}: VesselPlannerSettingsDialogProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [setting, setSetting] = useState<VesselPlannerSetting>(
    DEFAULT_VESSEL_PLANNER_SETTING
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSetting(
        normalizeVesselPlannerSetting(
          vessel?.plannerSettings ?? vessel?.plannerSetting
        )
      );
    }
  }, [open, vessel]);

  const floor = useMemo(
    () =>
      Array.isArray(vessel?.positions?.[0])
        ? vessel.positions[0].map((rows: any) =>
            Math.max(0, Number(rows) || 0)
          )
        : [],
    [vessel]
  );
  const dummies = useMemo(
    () =>
      new Set(
        (vessel?.dummies || [])
          .filter((position: any) => Array.isArray(position) && position.length >= 2)
          .map((position: any) =>
            plannerPositionKey(Number(position[0]), Number(position[1]))
          )
      ),
    [vessel]
  );
  const excluded = useMemo(
    () =>
      new Set(
        setting.excludedPositions.map(([x, y]) => plannerPositionKey(x, y))
      ),
    [setting]
  );
  const validPositions = useMemo(() => {
    const result: [number, number][] = [];
    floor.forEach((rows: number, columnIndex: number) => {
      getUsableVesselSlots(rows).forEach((y) => {
        if (!dummies.has(plannerPositionKey(columnIndex + 1, y))) {
          result.push([columnIndex + 1, y]);
        }
      });
    });
    return result;
  }, [dummies, floor]);

  const setExcluded = (keys: Set<string>) => {
    const excludedPositions = validPositions.filter(([x, y]) =>
      keys.has(plannerPositionKey(x, y))
    );
    setSetting((current) => ({ ...current, excludedPositions }));
  };

  const sequenceNumbers = useMemo(
    () =>
      new Map(
        setting.stackingSequence.map(([x, y], index) => [
          plannerPositionKey(x, y),
          index + 1,
        ])
      ),
    [setting.stackingSequence]
  );

  const toggleSequencePosition = (x: number, y: number) => {
    const key = plannerPositionKey(x, y);
    if (dummies.has(key) || excluded.has(key)) return;
    setSetting((current) => {
      const alreadySelected = current.stackingSequence.some(
        ([selectedX, selectedY]) => plannerPositionKey(selectedX, selectedY) === key
      );
      return {
        ...current,
        stackingSequence: alreadySelected
          ? current.stackingSequence.filter(
              ([selectedX, selectedY]) =>
                plannerPositionKey(selectedX, selectedY) !== key
            )
          : [...current.stackingSequence, [x, y]],
      };
    });
  };

  const updateConstraint = (
    key: keyof VesselPlannerSetting["constraints"],
    checked: boolean
  ) => {
    setSetting((current) => ({
      ...current,
      constraints: { ...current.constraints, [key]: checked },
    }));
  };

  const autoFillSequence = () => {
    const availablePositions = validPositions.filter(
      ([x, y]) => !excluded.has(plannerPositionKey(x, y))
    );
    const rows = Array.from(
      new Set(availablePositions.map(([, y]) => y))
    );
    const rowCenter =
      rows.length > 0 ? (Math.min(...rows) + Math.max(...rows)) / 2 : 0;
    rows.sort((left, right) => {
      const leftDistance = Math.abs(left - rowCenter);
      const rightDistance = Math.abs(right - rowCenter);
      const distanceComparison =
        setting.incompleteLayerPreference === "middleRowsFirst"
          ? leftDistance - rightDistance
          : rightDistance - leftDistance;
      return distanceComparison || right - left;
    });

    const rowOrder = new Map(rows.map((row, index) => [row, index]));
    availablePositions.sort((left, right) => {
      const rowComparison = rowOrder.get(left[1])! - rowOrder.get(right[1])!;
      if (rowComparison !== 0) return rowComparison;
      return left[0] - right[0];
    });

    setSetting((current) => ({
      ...current,
      stackingSequence: availablePositions,
      showSequenceNumbers: true,
    }));
  };

  const togglePosition = (x: number, y: number) => {
    const key = plannerPositionKey(x, y);
    if (dummies.has(key)) return;
    const next = new Set(excluded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setExcluded(next);
  };

  const save = async () => {
    if (!vessel?.["@id"] || saving) return;
    const validKeys = new Set(
      validPositions.map(([x, y]) => plannerPositionKey(x, y))
    );
    const settingToSave = {
      ...setting,
      stackingSequence: setting.stackingSequence.filter(([x, y]) => {
        const key = plannerPositionKey(x, y);
        return validKeys.has(key) && !excluded.has(key);
      }),
    };
    setSaving(true);
    try {
      const vesselPayload = { ...vessel, plannerSettings: settingToSave };
      delete vesselPayload.plannerSetting;
      const updated = await (dispatch as any)(
        updateVessel(vesselPayload)
      );
      onSaved?.(updated || vesselPayload);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: "min(98vw, 1480px)",
          maxHeight: "92vh",
          borderRadius: 2.5,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle sx={{ px: 2.5, py: 1.75 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              display: "grid",
              placeItems: "center",
              width: 42,
              height: 42,
              borderRadius: 1,
              bgcolor: "primary.main",
              color: "primary.contrastText",
            }}
          >
            <SettingsOutlinedIcon />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: "1.35rem", fontWeight: 900, lineHeight: 1.2 }}>
              {t("vesselSettingsTitle", "Autoplanner – Vessel settings")}
            </Typography>
            <Typography sx={{ color: "text.secondary", fontSize: "0.82rem", mt: 0.25 }}>
              {t(
                "vesselSettingsSubtitle",
                "Configure autostacker preferences and constraints for this vessel."
              )}
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={saving} aria-label={t("close")}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <Divider />
      <Tabs value={0} sx={{ px: 2.5, minHeight: 48 }}>
        <Tab label={t("vesselSettingsExclusionsTitle", "1. Exclusions")} sx={{ minHeight: 48, textTransform: "none", fontWeight: 800 }} />
      </Tabs>
      <Divider />

      <DialogContent sx={{ bgcolor: "#f8fafc", p: { xs: 1.5, md: 2.5 } }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
            gap: 1.5,
          }}
        >
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ fontWeight: 900, color: "#172554" }}>
                  {t("vesselSettingsExclusionsTitle", "1. Exclusions")}
                </Typography>
                <Tooltip
                  title={t(
                    "vesselSettingsExclusionsHint",
                    "Select positions that should not be used for stacking."
                  )}
                >
                  <InfoOutlinedIcon color="primary" sx={{ fontSize: 18, cursor: "help" }} />
                </Tooltip>
              </Stack>
            </Box>
            <Stack direction="row" spacing={2} alignItems="center">
              {[
                ["#eff8ea", "available"],
                ["#d6d9df", "excluded"],
              ].map(([color, label]) => (
                <Stack key={label} direction="row" spacing={0.6} alignItems="center">
                  <Box sx={{ width: 15, height: 15, bgcolor: color, border: "1px solid #cbd5e1" }} />
                  <Typography sx={{ fontSize: "0.72rem" }}>{t(label)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>

          {floor.length === 0 ? (
            <Typography sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
              {t(
                "vesselSettingsNoGrid",
                "Configure the vessel grid before selecting exclusions."
              )}
            </Typography>
          ) : (
            <Box sx={{ mt: 1.25 }}>
              <VesselSettingsGrid
                vessel={vessel}
                excluded={excluded}
                onPositionClick={togglePosition}
              />
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={1.25} sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={() => setExcluded(new Set())} sx={{ textTransform: "none" }}>
              {t("clearAll", "Clear all")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                const next = new Set<string>();
                validPositions.forEach(([x, y]) => {
                  const key = plannerPositionKey(x, y);
                  if (!excluded.has(key)) next.add(key);
                });
                setExcluded(next);
              }}
              sx={{ textTransform: "none" }}
            >
              {t("invertSelection", "Invert selection")}
            </Button>
          </Stack>
        </Paper>
        <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 2 }}>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography sx={{ fontWeight: 900, color: "#172554" }}>
                  {t("vesselSettingsSequenceTitle", "2. Sequence of stacking")}
                </Typography>
                <Tooltip
                  title={t(
                    "vesselSettingsSequenceHint",
                    "Define the order in which positions are considered by the autoplanner."
                  )}
                >
                  <InfoOutlinedIcon color="primary" sx={{ fontSize: 18, cursor: "help" }} />
                </Tooltip>
              </Stack>
            </Box>
            <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="nowrap">
              <Button
                size="small"
                variant="contained"
                onClick={autoFillSequence}
                disabled={validPositions.every(([x, y]) =>
                  excluded.has(plannerPositionKey(x, y))
                )}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                {t("autoSequence", "Auto sequence")}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setSetting((current) => ({ ...current, stackingSequence: [] }))
                }
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                {t("clearSequence", "Clear sequence")}
              </Button>
              <Switch
                size="small"
                checked={setting.showSequenceNumbers}
                onChange={(_, checked) =>
                  setSetting((current) => ({
                    ...current,
                    showSequenceNumbers: checked,
                  }))
                }
              />
              <Typography sx={{ fontSize: "0.7rem", whiteSpace: "nowrap" }}>
                {t("showNumbers", "Show numbers")}
              </Typography>
            </Stack>
          </Stack>

          <Box sx={{ mt: 1.25 }}>
            <VesselSettingsGrid
              vessel={vessel}
              excluded={excluded}
              sequenceNumbers={sequenceNumbers}
              showSequenceNumbers={setting.showSequenceNumbers}
              disableExcluded
              onPositionClick={toggleSequencePosition}
            />
          </Box>
          <Box sx={{ mt: 1, p: 1, display: "flex", gap: 1, bgcolor: "#eff6ff", color: "#1d4ed8", borderRadius: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.72rem" }}>
              {t(
                "vesselSettingsSequenceInfo",
                "Click an available cell to add it to the sequence. Click it again to remove it."
              )}
            </Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontWeight: 900, color: "#172554" }}>
              {t("vesselSettingsGroupingTitle", "3. Preference for grouping")}
            </Typography>
            <Tooltip
              title={t(
                "vesselSettingsGroupingHint",
                "How should containers be grouped by destination?"
              )}
            >
              <InfoOutlinedIcon color="primary" sx={{ fontSize: 18, cursor: "help" }} />
            </Tooltip>
          </Stack>
          <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 1 }}>
            {GROUPING_OPTIONS.map(({ value, labelKey, descriptionKey, preview }) => (
              <Box
                key={String(value)}
                component="button"
                type="button"
                onClick={() =>
                  setSetting((current) => ({
                    ...current,
                    groupingPreference: value,
                  }))
                }
                sx={{ p: 1.25, textAlign: "left", bgcolor: "#fff", border: "1px solid", borderColor: setting.groupingPreference === value ? "primary.main" : "divider", borderRadius: 1.5, cursor: "pointer" }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Radio size="small" checked={setting.groupingPreference === value} sx={{ p: 0 }} />
                  <Typography sx={{ fontSize: "0.76rem", fontWeight: 850 }}>{t(labelKey)}</Typography>
                </Stack>
                <Box sx={{ my: 1, display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gridTemplateRows: "repeat(4, 15px)", gap: "2px" }}>
                  {preview.map((color, index) => (
                    <Box key={index} sx={{ bgcolor: color, border: "1px solid rgba(71,85,105,.18)", borderRadius: 0.3 }} />
                  ))}
                </Box>
                <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", lineHeight: 1.35 }}>
                  {t(descriptionKey)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.25, p: 1, display: "flex", gap: 1, bgcolor: "#eff6ff", color: "#1d4ed8", borderRadius: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.72rem" }}>
              {t(
                "vesselSettingsGroupingInfo",
                "These preferences guide how destinations are distributed over available floor space."
              )}
            </Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontWeight: 900, color: "#172554" }}>
              {t(
                "vesselSettingsIncompleteTitle",
                "4. Preference for incomplete layers"
              )}
            </Typography>
            <Tooltip
              title={t(
                "vesselSettingsIncompleteHint",
                "If the top layer is not full, prefer stacking on:"
              )}
            >
              <InfoOutlinedIcon color="primary" sx={{ fontSize: 18, cursor: "help" }} />
            </Tooltip>
          </Stack>
          <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1 }}>
            {[
              ["middleRowsFirst", "autoPlannerIncomplete_middleRowsFirst", "autoPlannerIncomplete_middleRowsFirst_desc"],
              ["outerRowsFirst", "autoPlannerIncomplete_outerRowsFirst", "autoPlannerIncomplete_outerRowsFirst_desc"],
            ].map(([value, labelKey, descriptionKey]) => (
              <Box
                key={value}
                component="button"
                type="button"
                onClick={() =>
                  setSetting((current) => ({
                    ...current,
                    incompleteLayerPreference: value as VesselPlannerSetting["incompleteLayerPreference"],
                  }))
                }
                sx={{ p: 1.25, textAlign: "left", bgcolor: "#fff", border: "1px solid", borderColor: setting.incompleteLayerPreference === value ? "primary.main" : "divider", borderRadius: 1.5, cursor: "pointer" }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Radio size="small" checked={setting.incompleteLayerPreference === value} sx={{ p: 0 }} />
                  <Typography sx={{ fontSize: "0.76rem", fontWeight: 850 }}>{t(labelKey)}</Typography>
                </Stack>
                <Box sx={{ my: 1, display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gridTemplateRows: "repeat(4, 14px)", gap: "2px" }}>
                  {Array.from({ length: 28 }, (_, index) => {
                    const row = Math.floor(index / 7);
                    const active = value === "middleRowsFirst" ? row === 1 || row === 2 : row === 0 || row === 3;
                    return <Box key={index} sx={{ bgcolor: active ? "#60a5fa" : "#dbe1e8", border: "1px solid #fff" }} />;
                  })}
                </Box>
                <Typography sx={{ fontSize: "0.66rem", color: "text.secondary" }}>{t(descriptionKey)}</Typography>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.25, p: 1, display: "flex", gap: 1, bgcolor: "#eff6ff", color: "#1d4ed8", borderRadius: 1 }}>
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.72rem" }}>
              {t(
                "vesselSettingsIncompleteInfo",
                "This preference is applied to the top incomplete layer."
              )}
            </Typography>
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, gridColumn: { lg: "1 / -1" } }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography sx={{ fontWeight: 900, color: "#172554" }}>
              {t("vesselSettingsConstraintsTitle", "5. Constraints")}
            </Typography>
            <Tooltip
              title={t(
                "vesselSettingsConstraintsHint",
                "Define the constraints the autoplanner must respect."
              )}
            >
              <InfoOutlinedIcon color="primary" sx={{ fontSize: 18, cursor: "help" }} />
            </Tooltip>
          </Stack>
          <Box sx={{ mt: 1.25, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1 }}>
            {[
              ["respectFortyOnTwenty", "autoPlannerConstraintRespectFortyOnTwenty", "autoPlannerConstraintRespectFortyOnTwenty_desc", <VerticalAlignTopOutlinedIcon />],
              ["balanceOverBeam", "autoPlannerConstraintBalanceBeam", "autoPlannerConstraintBalanceBeam_desc", <BalanceOutlinedIcon />],
              ["balanceOverLength", "autoPlannerConstraintBalanceLength", "autoPlannerConstraintBalanceLength_desc", <DirectionsBoatOutlinedIcon />],
              ["segregateAdn", "autoPlannerConstraintSegregateAdn", "autoPlannerConstraintSegregateAdn_desc", <WarningAmberOutlinedIcon />],
              ["avoidShifters", "autoPlannerConstraintAvoidShifters", "autoPlannerConstraintAvoidShifters_desc", <ViewColumnOutlinedIcon />],
            ].map(([key, labelKey, descriptionKey, icon]) => {
              const constraintKey = key as keyof VesselPlannerSetting["constraints"];
              const disabled = constraintKey === "respectFortyOnTwenty";
              return (
                <Box key={String(key)} sx={{ p: 1.1, minHeight: 150, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
                  <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                    <Checkbox
                      size="small"
                      checked={setting.constraints[constraintKey]}
                      disabled={disabled}
                      onChange={(_, checked) => updateConstraint(constraintKey, checked)}
                      sx={{ p: 0 }}
                    />
                    <Typography sx={{ fontSize: "0.73rem", fontWeight: 850 }}>{t(labelKey)}</Typography>
                  </Stack>
                  <Box sx={{ my: 1.3, display: "grid", placeItems: "center", color: "primary.main", "& svg": { fontSize: 42 } }}>{icon}</Box>
                  <Typography sx={{ textAlign: "center", fontSize: "0.65rem", color: "text.secondary" }}>{t(descriptionKey)}</Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: "space-between" }}>
        <Button
          variant="outlined"
          onClick={() => setSetting(DEFAULT_VESSEL_PLANNER_SETTING)}
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {t("resetToDefaults", "Reset to defaults")}
        </Button>
        <Stack direction="row" spacing={1.25}>
          <Button variant="outlined" onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
            {t("cancel")}
          </Button>
          <Button variant="contained" onClick={save} disabled={saving || !vessel?.["@id"]} sx={{ textTransform: "none", minWidth: 140 }}>
            {saving ? t("saving") : t("saveSettings", "Save settings")}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default VesselPlannerSettingsDialog;
